import { useEffect, useState } from 'react';
import { EmailAttachment } from '@/api/submission-scheduler/email-attachments';
import {
	Email,
	EmailSchema,
	getById,
	update,
} from '@/api/submission-scheduler/emails';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DateTimePicker } from '@/components/ui/custom/datetime-picker';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MinimalTiptapEditor } from '@/components/ui/minimal-tiptap';
import { Textarea } from '@/components/ui/textarea';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { AttachmentTable } from './email-attachments/table';

export function EditEmailDialog({ email }: { email: Email }) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const [currentAttachments, setCurrentAttachments] = useState<
		EmailAttachment[]
	>(email.emailAttachments ?? []);

	const [hasAttachmentChanges, setHasAttachmentChanges] = useState(false);

	const refetchEmail = async () => {
		try {
			const updatedEmail = await getById(email.id);
			console.log(updatedEmail);
			if (updatedEmail && updatedEmail.emailAttachments) {
				setCurrentAttachments(updatedEmail.emailAttachments);
				setHasAttachmentChanges(true);
			}
		} catch (error) {
			console.error('Failed to refetch email attachments:', error);
		}
	};

	useEffect(() => {
		if (open) {
			setCurrentAttachments(email.emailAttachments ?? []);
			setHasAttachmentChanges(false);
		}
	}, [open, email.emailAttachments]);

	const { mutate } = useMutation({
		mutationFn: (editedEmail: Email) => update(editedEmail),
		onSuccess: () => {
			toast.success(`Success! Email has been successfully updated.`, {
				dismissible: true,
			});
			setHasAttachmentChanges(false);
			form.reset();
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: ['submission-scheduler'],
			});
		},
		onError: () => {
			toast.error(`Error! Email could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<Email>({
		resolver: zodResolver(EmailSchema),
		defaultValues: email,
		mode: 'all',
	});

	const hasUnsavedChanges = hasAttachmentChanges || form.formState.isDirty;

	const onSubmit: SubmitHandler<Email> = (values: Email) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Edit email</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Dialog
				open={open}
				onOpenChange={(newOpen) => {
					if (!newOpen && hasAttachmentChanges) {
						return;
					}
					setOpen(newOpen);
				}}
			>
				<DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
					<DialogHeader>
						<DialogTitle>Edit email</DialogTitle>
						<DialogDescription>
							Make changes to the email and click save when done.
						</DialogDescription>
					</DialogHeader>

					{hasUnsavedChanges && (
						<Alert variant="destructive" className="m-auto">
							<div className="flex items-start gap-3">
								<AlertCircle className="mt-0.5 h-4 w-4" />
								<AlertDescription className="flex-1">
									You have unsaved changes. Please click "Save changes" before closing
									this dialog.
								</AlertDescription>
							</div>
						</Alert>
					)}

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="flex-1 overflow-y-auto"
						>
							<div className="space-y-4 py-6 pr-2">
								<FormField
									control={form.control}
									name="sender"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Sender</FormLabel>
											<FormControl>
												<Input {...field} disabled={true} value={field.value ?? ''} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="recipient"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Recipient</FormLabel>
											<FormControl>
												<Input
													{...field}
													value={field.value ?? ''}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="ccList"
									render={({ field }) => (
										<FormItem>
											<FormLabel>CC Recipients</FormLabel>
											<FormDescription>
												Separate multiple email addresses with semicolons, commas, or spaces
											</FormDescription>
											<FormControl>
												<Textarea
													{...field}
													placeholder="email1@example.com; email2@example.com"
													value={field.value ?? ''}
													onChange={(e) => {
														let value = e.target.value;
														// Replace commas, newlines, and tabs with semicolons
														value = value.replace(/[,\n\r\t]+/g, '; ');
														// Replace multiple spaces with a single semicolon and space
														value = value.replace(/\s{2,}/g, '; ');
														// Clean up extra semicolons and spaces
														value = value.replace(/;\s*;+/g, ';');
														value = value.replace(/;\s+/g, '; ');
														field.onChange(value);
													}}
													onBlur={(e) => {
														let value = e.target.value;
														// Replace any remaining spaces between emails with semicolons
														value = value.replace(/\s+/g, '; ');
														// Clean up multiple semicolons
														value = value.replace(/;\s*;+/g, '; ');
														// Remove trailing semicolon
														value = value.replace(/;\s*$/g, '');
														field.onChange(value);
														field.onBlur();
													}}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="isBCC"
									render={({ field }) => (
										<FormItem className="flex flex-row items-start space-x-3 space-y-0">
											<FormControl>
												<Checkbox checked={field.value} onCheckedChange={field.onChange} />
											</FormControl>
											<div className="space-y-1 leading-none">
												<FormLabel>BCC my email address</FormLabel>
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="subject"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Subject</FormLabel>
											<FormControl>
												<Input
													{...field}
													value={field.value ?? ''}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<AttachmentTable
									emailId={email.id}
									attachments={currentAttachments}
									onAttachmentsChanged={refetchEmail}
								/>

								<FormField
									control={form.control}
									name="deliveryDateTime"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Delivery Date Time</FormLabel>
											<FormDescription>
												Time will be saved in Eastern Standard Time (EST)
											</FormDescription>
											<FormControl>
												<DateTimePicker
													value={field.value ?? null}
													onChange={field.onChange}
													placeholder="Select delivery date and time"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="body"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Body</FormLabel>
											<FormControl>
												<MinimalTiptapEditor
													value={field.value}
													onChange={field.onChange}
													className="w-full"
													editorContentClassName="p-4"
													allowImages={false}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div className="flex justify-end gap-2 pt-4">
									<Button
										type="button"
										variant="outline"
										onClick={() => setOpen(false)}
										disabled={hasAttachmentChanges}
									>
										Cancel
									</Button>
									<Button type="submit" disabled={form.formState.isSubmitting}>
										{form.formState.isSubmitting ? 'Saving...' : 'Save changes'}
									</Button>
								</div>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
